import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";
import queryClient from "core/queryClient";
import User, { dtoToUser } from "models/user";
import ManagementBackendClient from "services/management-backend/client";
import axios from "axios";

export enum Fields {
  REFRESH = "refresh",
  USER_ID = "user_id",
  TOKEN = "token",
}

const getTokens = (): AuthorizationState | undefined => {
  const refresh = localStorage.getItem(Fields.REFRESH);
  const user = localStorage.getItem(Fields.USER_ID);
  const token = localStorage.getItem(Fields.TOKEN);

  if (refresh && user && token)
    return { refresh, user_id: Number(user), token };
  return undefined;
};

const setTokens = (token: string, refresh: string, userId: number) => {
  localStorage.setItem(Fields.USER_ID, String(userId));
  localStorage.setItem(Fields.REFRESH, refresh);
  localStorage.setItem(Fields.TOKEN, token);
};

const clearTokens = () => {
  localStorage.removeItem(Fields.REFRESH);
  localStorage.removeItem(Fields.USER_ID);
  localStorage.removeItem(Fields.TOKEN);
};

export const StateContext = createContext<State>({
  tokens: getTokens(),
});

const StateDispatcherContext = createContext<React.Dispatch<ContextAction>>(
  () => {},
);

export type State = {
  tokens?: AuthorizationState;
  user?: User;
};

export type AuthorizationState = {
  refresh: string;
  user_id: number;
  token: string;
};

export enum AuthActionTypes {
  SET_TOKEN,
  SET_USER,
  LOGOUT,
  LOGIN,
}

type LoginAction = {
  type: AuthActionTypes.LOGIN;
  tokens: AuthorizationState;
  user: User;
};

type LogoutAction = {
  type: AuthActionTypes.LOGOUT;
};

type SetTokenAction = {
  type: AuthActionTypes.SET_TOKEN;
  tokens: AuthorizationState;
};

type SetUser = {
  type: AuthActionTypes.SET_USER;
  user: User;
};

type ContextAction = LoginAction | LogoutAction | SetTokenAction | SetUser;

const reducer = (current: State, action: ContextAction): State => {
  switch (action.type) {
    case AuthActionTypes.SET_TOKEN:
      setTokens(
        action.tokens.token,
        action.tokens.refresh,
        action.tokens.user_id,
      );
      return { tokens: { ...action.tokens } };
    case AuthActionTypes.SET_USER:
      return { user: action.user, ...current };
    case AuthActionTypes.LOGIN:
      setTokens(
        action.tokens.token,
        action.tokens.refresh,
        action.tokens.user_id,
      );
      return { tokens: action.tokens, user: action.user };
    case AuthActionTypes.LOGOUT:
      queryClient.clear();
      clearTokens();
      return {};
  }
};

export const StateProvider = ({ children }: PropsWithChildren) => {
  let tokens = getTokens();
  const [state, dispatcher] = useReducer(reducer, { tokens });
  useEffect(() => {
    if (tokens) {
      const instance = axios.create({
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: tokens.token,
        },
        baseURL: import.meta.env.VITE_MANAGEMENT_URL,
      });

      instance.interceptors.request.use((config) => {
        config.headers.Authorization = tokens?.token;
        return config;
      });

      const client = new ManagementBackendClient(instance);
      client.getOrganizationUser(tokens.user_id).then((res) => {
        dispatcher({ type: AuthActionTypes.SET_USER, user: dtoToUser(res) });
      });
    }
  }, []);
  return (
    <StateContext.Provider value={state}>
      <StateDispatcherContext.Provider value={dispatcher}>
        {children}
      </StateDispatcherContext.Provider>
    </StateContext.Provider>
  );
};

export const useStateContext = () => {
  return useContext(StateContext);
};

export const useStateDispatcher = () => {
  return useContext(StateDispatcherContext);
};
